import { useState, useEffect, useRef, useCallback } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
    Account, useAppSelector, DriveRequest, ErrorStackItem, Message, getEnumKey, MessageHandlerDisplayType, useSendRequestMutation, DriveErrorMessages,
    CollectionFilesyncAPI, DriveWatchedErrorMessages, useAppDispatch, PermissionSetType, useGetPermissions, NodeIdentifier, FullNodePath
} from "src/common";
import { uiActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

export function useGetPath(current_account: Account, watched: boolean = false) {
    const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
    const [node_identifiers, setNodeIdentifier] = useState<NodeIdentifier>();
    const [full_path_list, setFullPathList] = useState<FullNodePath | undefined>();
    const [watched_node_ids, setWatchedNodeIds] = useState<string[] | undefined>();
    const [error, setError] = useState<boolean>(false);
    const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
    const [sendRequest] = useSendRequestMutation();
    const dispatch = useAppDispatch();

    // Description: Allow active permissions and default_permissions to be accessed
    const default_permissions_ref = useRef<PermissionSetType[]>(default_permissions);
    useEffect(() => {
        default_permissions_ref.current = default_permissions;
    }, [default_permissions]);

    // Description: Handle the name field in the watch node list
    useEffect(() => {
        if (!!permissions && !!node_identifiers) {
            handleGetPath(node_identifiers.collection_id, node_identifiers.id, permissions);
            !!watched && handleGetWatchedPath(node_identifiers.collection_id, node_identifiers.id, permissions);
        }

    }, [permissions]);

    // Description: Fetch all watched root nodes
    async function handleGetPath(_collection_id: string, _id: string, _permissions: PermissionSetType) {
        const drive_request = !!_permissions ? await CollectionFilesyncAPI.getPath(current_account, _collection_id, _id, _permissions) : null;
        async function callback(message: FSMessage) {
            destroyPermissions();
            message.getStatus() === FSStatus.OK ?
                setFullPathList(message.getFullNodePath()) :
                handlePageErrorMessage(DriveErrorMessages.default, { stack_error: message.toObject(), stack_message: DriveWatchedErrorMessages.error_fetching_watched_root_nodes }, message, MessageHandlerDisplayType.logger);
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_watched_root_nodes);
    }

    // Description: Fetch all watched root nodes
    async function handleGetWatchedPath(_collection_id: string, _id: string, _permissions: PermissionSetType) {
        const drive_request = !!_permissions ? await CollectionFilesyncAPI.getWatchedNodePath(current_account, _collection_id, _id, _permissions) : null;
        async function callback(message: FSMessage) {
            destroyPermissions();
            if (message.getStatus() === FSStatus.OK) {
                const list = message.getWatchedNodePath()?.getPathList();
                (!!list && list.length > 0) &&
                    setWatchedNodeIds(_.map(list, (node: FSMessage.Dir.PathNode) => (node.getId_asB64())));
            } else {
                handlePageErrorMessage(DriveErrorMessages.default, { stack_error: message.toObject(), stack_message: DriveWatchedErrorMessages.error_fetching_watched_root_nodes }, message, MessageHandlerDisplayType.logger);
            }

        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_watched_root_nodes);
    }

    // -----------------------------
    // HOOK General
    // -----------------------------
    // Description: Sends Request and handles error when the request object is null, pass it custom stack message
    function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>,
        stack_error: string = DriveErrorMessages.error_sending_request, user_message: string = DriveErrorMessages.default) {
        if (!!request) {
            request.setCallback(callback);
            sendRequest(request);
        } else {
            handlePageErrorMessage(user_message, { stack_error, stack_message: DriveErrorMessages.error_sending_request }, null, MessageHandlerDisplayType.toastr);
        }
    }

    // Description: Handle error message and send error to store
    function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
        display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useGetPath Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setError(true);
    }

    // Callbacks
    // -----------------------------
    // Description: Callback for browse directory
    const getPath = useCallback((collection_id: string, id: string) => {
        setNodeIdentifier({ collection_id, id });
        getPermissions(collection_id, default_permissions_ref.current);
    }, []);

    // Description: reset hook 
    const reset = useCallback(() => {
        setNodeIdentifier(undefined);
        setFullPathList(undefined);
        setError(false);
        destroyPermissions();
    }, []);

    return {
        getPath,
        reset,
        full_path_list,
        watched_node_ids,
        error
    };
}